import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useMaestroStore } from '../../store/maestroStore';
import { libraryService } from '../../services/libraryService';

const GOLD = '#C9A84C';

export default function EnrichmentProgress() {
  const enrichmentActive = useMaestroStore((s) => s.enrichment.active);
  const [dismissed, setDismissed] = useState(false);
  const runningRef = useRef(false);

  // Persistent state that survives across enrichment restarts
  const [phase, setPhase] = useState('idle'); // idle | scanning | enriching | done
  const [totalAlbums, setTotalAlbums] = useState(0);
  const [enrichedCount, setEnrichedCount] = useState(0);
  const [currentAlbum, setCurrentAlbum] = useState('');
  const pollRef = useRef(null);

  const pollProgress = useCallback(async () => {
    try {
      const resp = await libraryService.getAlbums({ limit: 1, offset: 0 });
      const total = Number(resp?.total) || 0;
      if (total === 0) return;

      const pendingResp = await libraryService.getAlbums({ status: 'pending', limit: 1, offset: 0 });
      const pending = Number(pendingResp?.total) || 0;
      const done = Math.max(0, total - pending);

      setTotalAlbums(total);
      setEnrichedCount(done);

      if (pending === 0) {
        setPhase('done');
        setTimeout(() => {
          setPhase('idle');
          setDismissed(false);
        }, 4000);
      }
    } catch { /* polling failure is fine */ }
  }, []);

  // Main enrichment loop
  useEffect(() => {
    if (!enrichmentActive || runningRef.current) return;
    runningRef.current = true;
    setDismissed(false);
    setPhase('enriching');

    (async () => {
      try {
        while (useMaestroStore.getState().enrichment.active) {
          const resp = await libraryService.getAlbums({ status: 'pending', limit: 500, offset: 0 });
          const pendingAlbums = resp?.albums || [];
          if (pendingAlbums.length === 0) {
            useMaestroStore.getState().finishEnrichment();
            break;
          }

          const pendingTotal = Number(resp?.total) || pendingAlbums.length;

          // Get total library size for the fraction display
          try {
            const allResp = await libraryService.getAlbums({ limit: 1, offset: 0 });
            const allTotal = Number(allResp?.total) || 0;
            if (allTotal > 0) {
              setTotalAlbums(allTotal);
              setEnrichedCount(Math.max(0, allTotal - pendingTotal));
            }
          } catch {}

          const keys = pendingAlbums.map((a) => a.album_key);
          const titles = {};
          for (const a of pendingAlbums) {
            titles[a.album_key] = a.title || a.album_key.split('/').pop();
          }

          useMaestroStore.getState().setEnrichmentQueue(keys, pendingTotal);

          for (const key of keys) {
            if (!useMaestroStore.getState().enrichment.active) break;
            const albumName = titles[key] || key.split('/').pop();
            setCurrentAlbum(albumName);
            useMaestroStore.getState().advanceEnrichment(albumName);
            try {
              await libraryService.enrichAlbum(key);
            } catch {}
            setEnrichedCount((prev) => prev + 1);
          }
        }
      } catch {
        useMaestroStore.getState().finishEnrichment();
      } finally {
        runningRef.current = false;
        setPhase('done');
        setCurrentAlbum('');
        setTimeout(() => setPhase('idle'), 4000);
      }
    })();
  }, [enrichmentActive]);

  // Poll progress every 5s while enriching (covers both enrichment paths)
  useEffect(() => {
    if (phase !== 'enriching') {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollProgress();
    pollRef.current = setInterval(pollProgress, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [phase, pollProgress]);

  if (phase === 'idle' || dismissed) return null;

  const pct = totalAlbums > 0 ? Math.round((enrichedCount / totalAlbums) * 100) : 0;

  if (phase === 'done') {
    return (
      <div style={{
        margin: '0 16px 12px', padding: '10px 16px', borderRadius: 12,
        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(91,164,229,0.2)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14 }}>✓</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#5BA4E5' }}>
            Library cataloged — {totalAlbums} album{totalAlbums !== 1 ? 's' : ''} with cover art & metadata
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      margin: '0 16px 12px', padding: '12px 16px', borderRadius: 12,
      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
      backdropFilter: 'blur(20px)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 16, height: 16, border: '2px solid rgba(255,255,255,0.15)',
            borderTop: `2px solid ${GOLD}`, borderRadius: '50%',
            animation: 'spin 0.8s linear infinite', flexShrink: 0,
          }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>
            Fetching cover art & metadata — {enrichedCount} of {totalAlbums} albums
          </span>
        </div>
        {totalAlbums - enrichedCount > 0 && (
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>
            {totalAlbums - enrichedCount} remaining
          </span>
        )}
      </div>

      <div style={{
        width: '100%', height: 6, borderRadius: 3,
        background: 'rgba(255,255,255,0.08)', overflow: 'hidden',
      }}>
        <div style={{
          height: '100%', borderRadius: 3, background: GOLD,
          width: `${Math.max(pct, 2)}%`,
          transition: 'width 0.5s ease',
        }} />
      </div>

      {currentAlbum && (
        <div style={{
          fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 4,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {currentAlbum}
        </div>
      )}
    </div>
  );
}
